import React, { useState, useEffect } from 'react';
import {
  KeyboardArrowLeftSharp,
  KeyboardArrowRightSharp,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { get } from 'lodash';
import Link from 'next/link';
import NumberFormat from 'react-number-format';
import cookie from 'js-cookie';

import {
  abbreviateState,
  addressString,
  checkStatusLive,
  formatAsDollars,
  formatBathrooms,
  formatOpenHouse,
  formatUrlAddress,
  getListingPhotoList,
  removeMissingPhotos,
} from '../../../lib/helpers';
import { MINIMUM_SUPPORTED_PRICE } from '../../../lib/constants';
import { isMobile } from '../../../lib/getDeviceSize';
import NumberElement from '../NumberElement';

import style from '../../../styles/sass/components/Swiper.module.scss';

interface Props {
  currentListingIndex: number;
  index: number;
  listing: any;
  showSwiperCard?: boolean;
}

const HEADER_HEIGHT = 70;
const BOTTOM_PADDING = 130;

const useStyles = makeStyles({
  photoCountText: {
    color: '#ffffff',
    fontSize: 18,
    letterSpacing: '0.84px',
    margin: '0 15px',
    textAlign: 'center',
    textShadow: '1px 1px 1px rgba(0, 0, 0, 0.75)',
  },
  photoNavButton: {
    backgroundColor: '#fff',
    borderRadius: '50%',
    boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.75)',
    height: 25,
    minWidth: 10,
    padding: 0,
    width: 25,
  },
});

function CarouselComponent(props: Props) {
  const classes = useStyles();
  const [currentPhotoIndex, setPhotoIndex] = useState(0);
  const [height, setHeight] = useState(500);
  const { currentListingIndex, index, listing } = props;
  const listingPhotos = removeMissingPhotos(
    getListingPhotoList(listing, isMobile() ? 'tall' : 'large'),
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress, false);
    if (typeof window !== 'undefined') {
      updateWindowDimensions();
    }
    return () => document.removeEventListener('keydown', handleKeyPress, false);
  });

  const updateWindowDimensions = () => {
    setHeight(window.innerHeight);
  };

  const handleKeyPress = (e: any) => {
    // N key
    if (e.keyCode === 78) {
      nextPhoto();
    }
    // B key
    if (e.keyCode === 66) {
      previousPhoto();
    }
  };

  const previousPhoto = () => {
    if (currentListingIndex !== index) {
      return;
    }
    if (currentPhotoIndex === 0) {
      setPhotoIndex(listingPhotos.length - 1);
    } else {
      setPhotoIndex(currentPhotoIndex - 1);
    }
  };
  const nextPhoto = () => {
    if (currentListingIndex !== index) {
      return;
    }
    if (currentPhotoIndex === listingPhotos.length - 1) {
      setPhotoIndex(0);
    } else {
      setPhotoIndex(currentPhotoIndex + 1);
    }
  };

  const openHouseOrUpdate = formatOpenHouse(listing);
  const activeStatus = checkStatusLive(listing);
  const contentHeight = height - HEADER_HEIGHT - BOTTOM_PADDING;
  const photoButtonDisabled = listingPhotos.length > 1 ? false : true;
  return (
    <React.Fragment>
      <div
        className={`${style.homeInner} home-inner row col-xs center-xs ${
          props.showSwiperCard ? 'no-mobile' : ''
        }`}
        key={`${listing.id} primary`}
        style={{ display: 'flex', flexDirection: 'column', padding: 0 }}
      >
        <div className={`${style.imageContainer} image-container`}>
          <img
            data-alt={`${listing.id} primary`}
            src={`${listingPhotos[currentPhotoIndex]}`}
            style={{
              background: `repeating-linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.8)), center center / cover no-repeat`,
              height: '100%',
              objectFit: 'cover',
              width: '100%',
            }}
          />
        </div>
        <div className={style.savingInfo}>
          <div className={style.openHouse}>
            <p className={style.openHouseText}>{openHouseOrUpdate}</p>
          </div>
        </div>
        <p className={`${style.photoCounterTop} mobile-only`}>
          {currentPhotoIndex + 1} / {listingPhotos.length}
        </p>
        <div
          className={`${style.homeContent} home-content row col-xs-12 center-xs bottom-xs`}
        >
          <div style={{ zIndex: 15 }}>
            <div className={`${style.contentLower} fadeInUp a3 d1`}>
              <div className={`${style.propertyLinkContainer} fadeIn a1`}>
                <Link
                  href={`/listing/r/${listing.id}/${formatUrlAddress(listing)}`}
                >
                  <a
                    className={style.propertyLink}
                    data-alt="Torii property link"
                    rel="noopener noreferrer"
                    style={{ display: 'block' }}
                    target="_blank"
                  >
                    <h1>
                      {addressString(listing, true)}
                      <br />
                      {listing.town}, {abbreviateState(listing.state)}{' '}
                      {listing.zipCode.substring(0, 5)}
                    </h1>
                    <h2 className={style.propertyDetails}>
                      <NumberElement
                        val={
                          listing.salePrice
                            ? listing.salePrice
                            : listing.listPrice
                        }
                        prefix="$"
                        decimalScale={0}
                      />
                      <span className="listing-pipe">|</span>
                      {listing.noBedrooms}br
                      <span className="listing-pipe">|</span>
                      {formatBathrooms(listing)}ba
                      <span className="listing-pipe">|</span>
                      {listing.squareFeet ? (
                        <NumberFormat
                          value={listing.squareFeet}
                          displayType="text"
                          thousandSeparator
                          decimalScale={0}
                        />
                      ) : (
                        '--'
                      )}
                      sf
                    </h2>
                  </a>
                </Link>
              </div>
            </div>
            <div className={`${style.photoCount} fadeInUp a3 d1 no-mobile`}>
              <Button
                className={classes.photoNavButton}
                disabled={photoButtonDisabled}
                onClick={previousPhoto}
              >
                <KeyboardArrowLeftSharp htmlColor="#323232" />
              </Button>
              <p className={classes.photoCountText}>
                {currentPhotoIndex + 1} / {listingPhotos.length}
              </p>
              <Button
                className={classes.photoNavButton}
                disabled={photoButtonDisabled}
                onClick={nextPhoto}
              >
                <KeyboardArrowRightSharp htmlColor="#323232" />
              </Button>
            </div>
          </div>
        </div>
        <div className={style.leftSide} onClick={previousPhoto} />
        <div className={style.rightSide} onClick={nextPhoto} />
      </div>
    </React.Fragment>
  );
}

export default CarouselComponent;
